import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
  GlassHeroContainer,
  GlassDropdownBox,
  GlassDropdownItem,
  NeumorphicButton,
  NeumorphicContainer,
  GlassShadows,
} from '../../glassmorphism/GlassmorphismStyles';

import { Category } from '../../../pages/Categories/CategoriesList';
import { getAllCategories } from '../../../pages/Categories/actions';
import { CategoryViewModel } from '../../../configuration/api/apiSchema';
import { HandymanAppState } from '../../../configuration/redux/reducers';
import HandymanMapWidget from '../../HandymanMapWidget/HandymanMapWidget';
import CollapsibleCategorySection from './CollapsibleCategorySection';
import GetStarted from '../../GetStarted/GetStarted';
import { http } from '../../../configuration/api/http';
import { getAuthData } from '../../../helpers/localStorageHelpers';
import { trackEvent } from '../../../analytics';
import HeroSkeleton from './skeletons/HeroSkeleton';

// Interface for user location data
interface UserLocation {
  city?: string;
  region?: string;
  country?: string;
}

export default function Hero() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  // State for user location
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);

  // Function to get user location from IP
  const getUserLocation = useCallback(async () => {
    try {
      const response = await http('https://ipwho.is/');
      if (response.ok) {
        const data = await response.json();
        if (data && data.success !== false) {
          setUserLocation({
            city: data.city,
            region: data.region,
            country: data.country,
          });
        }
      }
    } catch (error) {
      console.error('Error getting user location:', error);
    }
  }, []);

  // Get user location on component mount
  useEffect(() => {
    getUserLocation();
  }, [getUserLocation]);

  // Get categories from Redux store
  const { allCategories, isLoading } = useSelector(
    (state: HandymanAppState) => state.category,
  );

  const [searchTerm, setSearchTerm] = useState('');
  // This holds the dropdown matches (root + subcategories).
  const [dropdownResults, setDropdownResults] = useState<
    Array<{
      isRoot: boolean;
      categoryKey: string;
      categoryName: string;
      subKey?: string;
      subName?: string;
    }>
  >([]);

  // Controls showing/hiding the dropdown
  const [showDropdown, setShowDropdown] = useState(false);

  // Transform CategoryViewModel[] to Category[] for compatibility
  const transformCategories = useCallback(
    (categories: CategoryViewModel[] | undefined): Category[] => {
      if (!categories) return [];
      return categories.map((cat) => ({
        key: cat.key || '',
        name: cat.name || '',
        subcategories: transformCategories(cat.subcategories || []),
      }));
    },
    [],
  );

  const categories = useMemo(
    () => transformCategories(allCategories),
    [allCategories, transformCategories],
  );

  // Load all categories from backend on mount
  useEffect(() => {
    if (!allCategories && !isLoading) {
      dispatch(getAllCategories() as any);
    }
  }, []);

  // Build "dropdownResults" from the searchTerm (flat list of matches)
  useEffect(() => {
    const results: Array<{
      isRoot: boolean;
      categoryKey: string;
      categoryName: string;
      subKey?: string;
      subName?: string;
    }> = [];

    if (!searchTerm.trim()) {
      // Show all categories when search term is empty
      categories.forEach((cat) => {
        // Add root category
        results.push({
          isRoot: true,
          categoryKey: cat.key,
          categoryName: cat.name,
        });

        // Add all subcategories
        cat.subcategories?.forEach((sub) => {
          results.push({
            isRoot: false,
            categoryKey: cat.key,
            categoryName: cat.name,
            subKey: sub.key,
            subName: sub.name,
          });
        });
      });
    } else {
      // Filter categories based on search term
      const lowerTerm = searchTerm.toLowerCase();

      categories.forEach((cat) => {
        // Does the root category name match?
        if (cat.name.toLowerCase().includes(lowerTerm)) {
          results.push({
            isRoot: true,
            categoryKey: cat.key,
            categoryName: cat.name,
          });
        }

        // Check each subcategory
        cat.subcategories?.forEach((sub) => {
          if (sub.name.toLowerCase().includes(lowerTerm)) {
            results.push({
              isRoot: false,
              categoryKey: cat.key,
              categoryName: cat.name,
              subKey: sub.key,
              subName: sub.name,
            });
          }
        });
      });
    }

    setDropdownResults(results);
  }, [searchTerm, categories]);

  /**
   * Called when user selects either a root category or a subcategory
   * from the dropdown. We build a single query param named "selectedCategories"
   * with comma-separated values. E.g. "cleaning_services,window_washing"
   */
  const handleSelectDropdown = (
    isRoot: boolean,
    categoryKey: string,
    subKey?: string,
  ) => {
    trackEvent('hero_search_category', {
      category_key: categoryKey,
      subcategory_key: subKey,
      method: 'dropdown',
    });
    // Check user role - handymen should go to orders page instead of browse handyman
    // const authData = getAuthData();
    // if (authData?.role === "handyman") {
    //   navigate('/orders');
    //   return;
    // }

    // Build an array of category keys
    const selection: string[] = [categoryKey];
    if (!isRoot && subKey) {
      // If it's a subcategory, include both the root + sub
      selection.push(subKey);
    }

    // Build "selectedCategories" as a comma-separated string
    const paramValue = selection.join(',');
    // Example: "cleaning_services,window_washing"

    // Navigate with that single param
    navigate(
      `/browse-handyman?selectedCategories=${encodeURIComponent(paramValue)}`,
    );
  };

  /**
   * If user clicks a root category card in the grid, we do something similar:
   */
  const handleSelectCategory = (cat: Category) => {
    trackEvent('hero_search_category', {
      category_key: cat.key,
      method: 'grid',
    });
    // Check user role - handymen should go to orders page instead of browse handyman
    // const authData = getAuthData();
    // if (authData?.role === "handyman") {
    //   navigate('/orders');
    //   return;
    // }

    // Just the root
    const paramValue = cat.key;
    navigate(
      `/browse-handyman?selectedCategories=${encodeURIComponent(paramValue)}`,
    );

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const userDetails = useSelector((s: HandymanAppState) => s.user.userDetails);
  const authData = getAuthData();
  let isLoggedIn = !!authData;
  const shouldVerify =
    isLoggedIn &&
    ((userDetails?.kycStatus !== 'Verified' &&
      userDetails?.kycStatus !== undefined) ||
      (authData?.role === 'handyman' &&
        userDetails?.isStripeAccountVerified === false));

  if (!allCategories) {
    return <HeroSkeleton isDark />;
  }

  return (
    <GlassHeroContainer id='hero'>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: {
            xs: shouldVerify ? '244px' : '96px',
            sm: shouldVerify ? '252px' : '136px',
            tablet: '136px',
          },
          pb: { xs: '24px', sm: '48px' },
          overflowY: 'hidden',
        }}
      >
        <Stack
          useFlexGap
          sx={{
            alignItems: 'center',
            width: '100%',
            gap: { xs: '24px', sm: '48px' },
          }}
        >
          <Box sx={{ maxWidth: '100%' }}>
            <Typography
              component='h1'
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: { xs: 600, sm: 500 },
                fontSize: { xs: '34px', sm: '60px' },
                lineHeight: { xs: '124%', sm: '120%' },
                letterSpacing: { xs: '.25px', sm: '-0.5px' },
                textAlign: 'center',
                color: (theme) =>
                  theme.palette.mode === 'light' ? '#005CAD' : '#00FFFF',
                marginBottom: { xs: '16px', sm: '24px' },
              }}
            >
              Book Your Next
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '14px', sm: '16px' },
                lineHeight: { xs: '157%', sm: '175%' },
                letterSpacing: { xs: '.1px', sm: '0.15px' },
                textAlign: 'center',
                width: '956px',
                maxWidth: '100%',
                height: 'auto',
                color: (theme) =>
                  theme.palette.mode === 'light' ? '#1A1A1A' : '#B6B6B6',
              }}
            >
              Discover high-quality solutions tailored to your needs. From home
              repairs to specialized services, we connect you with skilled
              experts ready to get the job done.
            </Typography>
          </Box>

          {/* SEARCH BOX & BUTTON */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '760px',
                maxWidth: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <InputLabel htmlFor='search-hero' sx={visuallyHidden}>
                Search for a Service
              </InputLabel>
              <NeumorphicContainer
                sx={{
                  height: { xs: '36px', sm: '56px' },
                  width: '100%',
                  padding: { xs: '2px 2px 2px 16px', sm: '4px 4px 4px 24px' },
                  gap: '8px',
                }}
              >
                <TextField
                  id='search-hero'
                  variant='standard'
                  placeholder="e.g. 'electrical', 'plumbing', etc."
                  fullWidth
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearchTerm(e.target.value)
                  }
                  onFocus={() => setShowDropdown(true)}
                  onBlur={() => {
                    // Delay hiding so the onMouseDown in the dropdown can register
                    setTimeout(() => setShowDropdown(false), 150);
                  }}
                  sx={{
                    '& .MuiInput-root': {
                      '&:before': {
                        borderBottom: 'none',
                      },
                      '&:after': {
                        borderBottom: 'none',
                      },
                      '&:hover:not(.Mui-disabled):before': {
                        borderBottom: 'none',
                      },
                    },
                    '& .MuiInput-input': {
                      fontFamily: 'Montserrat, sans-serif',
                      fontWeight: 500,
                      textOverflow: 'ellipsis',
                      fontSize: { xs: '12px', sm: '16px' },
                      lineHeight: { xs: '24px', sm: '32px' },
                      color: (theme) =>
                        theme.palette.mode === 'light' ? '#2C2C2C' : '#fff',
                      padding: 0,
                      '&::placeholder': {
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? '#1A1A1A'
                            : 'rgba(213, 213, 213, 1)',
                      },
                    },
                  }}
                  slotProps={{
                    htmlInput: {
                      autoComplete: 'off',
                      'aria-label': 'Search services',
                    },
                  }}
                />
                <NeumorphicButton
                  sx={{
                    minWidth: { xs: '66px', sm: '104px' },
                    height: { xs: '32px', sm: '48px' },
                  }}
                >
                  Search
                </NeumorphicButton>
              </NeumorphicContainer>
            </Box>

            {/* DROPDOWN RESULTS */}
            {showDropdown && (
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: '44px', sm: '64px' },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '760px',
                  maxWidth: '100%',
                  zIndex: 999,
                  backgroundColor: 'rgba(59, 59, 59, 0.18)',
                  backdropFilter: 'blur(12px)',
                  padding: '8px 8px 8px 20px',
                  borderRadius: '32px',
                  boxShadow: GlassShadows.glassUp,
                }}
              >
                <GlassDropdownBox>
                  {dropdownResults.length > 0 ? (
                    dropdownResults.map((item, idx) =>
                      item.isRoot ? (
                        // Root category
                        <GlassDropdownItem
                          key={`root-${item.categoryKey}-${idx}`}
                          sx={{
                            fontFamily: 'Montserrat, sans-serif',
                            fontWeight: 800,
                            fontSize: '20px',
                            letterSpacing: '.15px',
                            lineHeight: '160%',
                            padding: { xs: '8px 24px', sm: '16px 32px' },
                          }}
                          onMouseDown={() =>
                            handleSelectDropdown(true, item.categoryKey)
                          }
                        >
                          {item.categoryName}
                        </GlassDropdownItem>
                      ) : (
                        // Subcategory
                        <GlassDropdownItem
                          key={`sub-${item.categoryKey}-${item.subKey}`}
                          onMouseDown={() =>
                            handleSelectDropdown(
                              false,
                              item.categoryKey,
                              item.subKey,
                            )
                          }
                          sx={{
                            fontSize: { xs: '14px', sm: '16px' },
                            fontWeight: 700,
                          }}
                        >
                          {item.subName}{' '}
                          <Typography
                            variant='body2'
                            component='span'
                            sx={{ ml: 0.5 }}
                          >
                            in {item.categoryName}
                          </Typography>
                        </GlassDropdownItem>
                      ),
                    )
                  ) : searchTerm.trim() !== '' ? (
                    // Show "No categories found" when searching but no results
                    <GlassDropdownItem
                      sx={{
                        fontStyle: 'italic',
                        textAlign: 'center',
                        color: '#585858',
                      }}
                    >
                      No categories found
                    </GlassDropdownItem>
                  ) : null}
                </GlassDropdownBox>
              </Box>
            )}
          </Box>

          {/* COLLAPSIBLE ROOT CATEGORIES (Before Map) */}
          <Box sx={{ width: '100%', maxWidth: '956px' }}>
            <CollapsibleCategorySection categories={categories} />
          </Box>

          {/* HANDYMAN MAP WIDGET */}
          <Box sx={{ width: '100%', maxWidth: '956px' }}>
            <Typography
              variant='h4'
              sx={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: { xs: 700, sm: 600 },
                fontSize: { xs: '24px', sm: '34px' },
                lineHeight: { xs: '133%', sm: '124%' },
                letterSpacing: { xs: 0, sm: '0.25px' },
                textAlign: 'center',
                mb: 2,
                color: (theme) =>
                  theme.palette.mode === 'light' ? '#005CAD' : '#00FFFF',
              }}
            >
              Find handyman near you
            </Typography>
            <Typography
              sx={{
                textAlign: 'center',
                mb: { xs: 3, sm: 6 },
                color: (theme) =>
                  theme.palette.mode === 'light' ? '#1A1A1A' : '#B6B6B6',
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '14px', sm: '16px' },
                lineHeight: { xs: '157%', sm: '175%' },
                letterSpacing: { xs: '.1px', sm: '0.15px' },
              }}
            >
              {userLocation
                ? `Discover skilled professionals in the ${userLocation.city}, ${userLocation.region} area`
                : 'Discover skilled professionals in your local area'}
            </Typography>

            <Box sx={{ height: { xs: 'auto', sm: '550px' } }}>
              <HandymanMapWidget />
            </Box>
          </Box>

          {/* GET STARTED CTA */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <GetStarted />
          </Box>

          {/* ALL SUBCATEGORIES GRID (After Map) - HIDDEN */}
          {/* <Box sx={{ width: '100%', mt: 6 }}>
            <AllSubcategoriesGrid categories={categories} />
          </Box> */}
        </Stack>
      </Container>
    </GlassHeroContainer>
  );
}
